/**
 * User Dropdown Menu Component
 *
 * Displays user info with dropdown for profile and logout actions.
 */

import { type ReactElement, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@features/auth';
import { ROUTES } from '@utils/constants';

interface UserDropdownProps {
  displayName: string;
  showLogout: boolean;
}

export function UserDropdown({
  displayName,
  showLogout,
}: UserDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleProfileClick = (): void => {
    setIsOpen(false);
    void navigate(ROUTES.SETTINGS);
  };

  const handleLogout = (): void => {
    setIsOpen(false);
    void logout();
  };

  return (
    <div className="user-dropdown" ref={dropdownRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        className="user-dropdown__trigger"
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="User menu"
      >
        <span className="user-dropdown__name">{displayName}</span>
        <span className="user-dropdown__chevron" aria-hidden="true">
          {isOpen ? '\u25B2' : '\u25BC'}
        </span>
      </button>

      {isOpen && (
        <div
          className="user-dropdown__menu"
          role="menu"
          aria-label="User actions"
        >
          <button
            type="button"
            className="user-dropdown__item"
            role="menuitem"
            onClick={handleProfileClick}
          >
            Profile
          </button>
          {showLogout && (
            <button
              type="button"
              className="user-dropdown__item user-dropdown__item--logout"
              role="menuitem"
              onClick={handleLogout}
            >
              Logout
            </button>
          )}
        </div>
      )}
    </div>
  );
}
